/**
 * Chess gameplay messages, layered on the shared envelope from
 * `@bn-games/network-protocol`. **This file is the single definition** — the
 * server side (`packages/game-sessions/src/chess/protocol.ts`) re-exports it,
 * which is why `chess/package.json` publishes `"./network/protocol"`.
 *
 * The server is **authoritative**: clients send intents (a UCI move, a draw
 * offer or answer) and never a colour — the server derives it from the
 * connection. State flows back only as `STATE_SYNC<ChessSnapshot>`. Chess is
 * full-information, so both seats receive the same snapshot. Resign, restart,
 * rematch and ping reuse the shared envelope.
 *
 * No DOM and no Node APIs here: the browser, the Node dev server and the
 * realtime Durable Object all import this module.
 */
import type {
  LobbyClientMessage,
  LobbyServerMessage,
  StateSyncMessage as EnvelopeStateSyncMessage,
} from '@bn-games/network-protocol';
import { GameState } from '../game/GameState.js';
import { remainingAt, type ClockState } from '../game/clock.js';
import { toFen } from '../game/fen.js';
import type { Outcome } from '../game/outcome.js';
import { FIDE, type RuleSet } from '../game/rules/index.js';
import type { Color } from '../game/types.js';

/** Seat roles in room order: slot 0 plays white until a rematch rotates them. */
export const SEATS = ['white', 'black'] as const;
export type SeatName = (typeof SEATS)[number];

export function colorOfSeat(name: string | undefined): Color | null {
  return name === 'white' ? 'w' : name === 'black' ? 'b' : null;
}

// --- Client-originated intents ---

export interface ChessMoveMessage {
  readonly type: 'CHESS_MOVE';
  /** "e2e4", "e7e8q". */
  readonly uci: string;
}

export interface ChessDrawOfferMessage {
  readonly type: 'CHESS_DRAW_OFFER';
}

export interface ChessDrawResponseMessage {
  readonly type: 'CHESS_DRAW_RESPONSE';
  readonly accept: boolean;
}

// --- Server-originated state ---

/**
 * Each side's time **as of the moment the snapshot was built**. The receiver
 * counts down from its own arrival time, so the two machines' wall clocks
 * never have to agree.
 */
export interface ChessClockSnapshot {
  readonly w: number;
  readonly b: number;
  /** Whose clock is running, or null while stopped (before both first moves, or game over). */
  readonly running: Color | null;
}

export interface ChessSnapshot {
  readonly startFen: string;
  /** Current position; redundant with `startFen` + `moves`, kept for a cheap sanity check. */
  readonly fen: string;
  /** Every move played, as UCI. */
  readonly moves: readonly string[];
  /** Null for an untimed room. */
  readonly clocks: ChessClockSnapshot | null;
  /** The colour with a draw offer on the table, if any. */
  readonly drawOfferFrom: Color | null;
  readonly outcome: Outcome | null;
}

export type StateSyncMessage = EnvelopeStateSyncMessage<ChessSnapshot>;

/**
 * `STATE_SYNC` is deliberately absent: the server never accepts state from a
 * client, so a forged snapshot is unrepresentable at the type level.
 */
export type ClientMessage =
  | LobbyClientMessage
  | ChessMoveMessage
  | ChessDrawOfferMessage
  | ChessDrawResponseMessage;

export type ServerMessage = LobbyServerMessage | StateSyncMessage;

function clockSnapshot(clock: ClockState, now: number): ChessClockSnapshot {
  return {
    w: remainingAt(clock, 'w', now),
    b: remainingAt(clock, 'b', now),
    running: clock.runningSince === null ? null : clock.turn,
  };
}

export function buildSnapshot(
  state: GameState,
  clock: ClockState | null,
  drawOfferFrom: Color | null,
  now: number,
): ChessSnapshot {
  return {
    startFen: state.startFen,
    fen: toFen(state.position),
    moves: state.uciMoves,
    clocks: clock === null ? null : clockSnapshot(clock, now),
    drawOfferFrom,
    outcome: state.outcome,
  };
}

/**
 * Rebuild the game a snapshot describes, or null if it does not replay —
 * a malformed FEN, an illegal move, or a final position that disagrees with
 * `fen`. An outcome the board itself cannot show (resignation, agreement,
 * flag) is applied on top.
 */
export function stateFromSnapshot(snapshot: ChessSnapshot, rules: RuleSet = FIDE): GameState | null {
  let state: GameState | null;
  try {
    state = GameState.fromUciMoves(snapshot.startFen, snapshot.moves, rules);
  } catch {
    return null;
  }
  if (state === null || toFen(state.position) !== snapshot.fen) return null;
  return snapshot.outcome !== null && state.outcome === null ? state.conclude(snapshot.outcome) : state;
}
